import React, {useCallback, useState, lazy, Suspense} from 'react';
import {Box, Typography, List, ListItem} from "@mui/joy";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import {useLoaderData} from "react-router-dom";
import NavigationBar from '../NavigationBar';
import * as styles from '../JoyStyleSheet';
import {useExperiment} from "@statsig/react-bindings";
import { Helmet } from 'react-helmet';
import {useIsMobile} from "../hooks/useIsMobile";
import MultiRoofForm from "../forms/roof/MultiRoofForm";

const BelowTheFoldContent = lazy(() => import('./BelowTheFoldContent'));

const IMAGE_PATH = '/images/';

interface Franchise {
    compass_location: string;
    twilio_phone?: string;
}

interface LoadedData {
    franchise: Franchise;
}

export default function LandingRoof() {
    const [slidesToShow, setSlidesToShow] = useState(
        typeof window !== "undefined" && window.innerWidth < 800 ? 1 : 2
    );
    const loadedData = useLoaderData() as LoadedData;
    const [franchise] = useState<Franchise>(loadedData?.franchise);
    const [selectedService] = useState<string>('Roof Clean');
    const headline_experiment = useExperiment("mobile_headline_2")
    const headline = headline_experiment.get("headline", "Get Your Quote");
    const isMobile = useIsMobile();

    const scrollToForm = useCallback(() => {
        const formElements = document.getElementsByClassName('form-submit-container');
        for (let i = 0; i < formElements.length; i++) {
            const el = formElements[i] as HTMLElement;
            const style = window.getComputedStyle(el);

            if (style.display !== 'none' && style.visibility !== 'hidden') {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }
        }
    }, []);

    const sliderSettings = {
        dots: false,
        arrows: false,
        infinite: true,
        autoplay: true,
        speed: 500,
        autoplaySpeed: 2000,
        slidesToShow,
        slidesToScroll: 1,
        lazyLoad:true,
        responsive: [
            {
                breakpoint: 800,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    initialSlide: 0,
                }
            }
        ]
    };

    const title = franchise?.compass_location
        ? `Yeti Clean ${franchise.compass_location} | Roof Cleaning Services`
        : 'Yeti Clean | Roof Cleaning Services';
    const description = franchise?.compass_location
        ? `Looking for roof cleaning near me in ${franchise.compass_location}? Yeti Clean offers professional roof cleaning services, full exterior cleaning, and guaranteed results.`
        : 'Looking for roof cleaning near me? Yeti Clean offers professional roof cleaning services, full exterior cleaning, and guaranteed results.';


    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link
                    rel="preload"
                    fetchPriority="high"
                    as="image"
                    href={`${IMAGE_PATH}top-banner-roof-2.webp`}
                    type="image/webp"
                />
            </Helmet>

            <Box>
                <Box sx={{...styles.parentBox}}>
                    <Box sx={(theme) => ({ ...styles.basicSize, marginTop: '15px', [theme.breakpoints.up('md')]: { marginBottom: '15px' } })}>
                        <NavigationBar selectedService={selectedService} franchise={franchise}/>
                    </Box>
                </Box>

                <Box sx={{ ...styles.parentBox, ...styles.sectionCustomMobileOnlyRoof }}>
                    <Box sx={{height: {xs: 'calc(80vh - 108px)'}, minHeight: '600px', width: '100%', display: 'flex', flexDirection: 'column'}}>
                        <Box
                            className="form-submit-container"
                            sx={{
                                position: 'relative',
                                height: '100%',
                                minHeight: '500px',
                                ...styles.parentBox,
                                ...styles.sectionCustomMobileOnlyRoof
                            }}
                        >
                            <img
                                src={`${IMAGE_PATH}top-banner-roof-2.webp`}
                                alt="A clean residential roof after professional cleaning services"
                                fetchPriority="high" // Prioritize fetching this image
                                loading="eager" // Ensure it's not lazy-loaded by default
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover', // Mimics background-size: cover
                                    zIndex: -1, // Places the image behind the content
                                }}
                            />
                            <Box>
                                <MultiRoofForm franchise={franchise}/>
                            </Box>
                        </Box>

                    </Box>
                </Box>

                <Box sx={{...styles.parentBox, ...styles.sectionCustomMobileOnlyRoof}}>
                    <Box sx={{...styles.basicSize, ...styles.oneHundred, ...styles.vCenter, ...styles.sectionCustomMobileOnlyRoof}}>
                        {/* OPTIMIZATION: Removed invalid attributes from the video tag */}
                        <video style={{paddingTop: "15px", width: '90%'}} controls poster={`${IMAGE_PATH}video-thumb-3.gif`}>
                            <source src="https://yeti-clean-media.lon1.cdn.digitaloceanspaces.com/before-after/roof_clean_og.mp4" type="video/mp4" />
                        </video>
                    </Box>
                </Box>

                <Box className="form-submit-container" sx={{...styles.parentBox, ...styles.sectionDesktopOnlyRoof}}>
                    <Box sx={{...styles.basicSize}}>
                        <Box sx={{...styles.paddingATF, ...styles.vLeft, ...styles.fiftyFiftyHidden}}>
                            <Box sx={{width: "100%"}}>
                                <Typography sx={{color: 'black'}} level="h1">Professional Roof Cleaning <br/> {franchise?.compass_location ? (
                                    <Typography sx={{color: 'black'}} >in {franchise.compass_location}</Typography>
                                ) : (
                                    <Typography >in the United Kingdom</Typography>
                                )}</Typography>


                            </Box>
                            <Box sx={{width: '100%'}}>
                                <Typography variant="soft"  sx={{pl: 0, pr:0, ml: 0, mr: 0, textAlign: 'center', width: '100%', boxSizing: 'border-box'}} level="h3" color={"success"}>Including a <Typography sx={{fontWeight: 700}}>FREE Full Exterior Clean</Typography> Worth £400</Typography>
                            </Box>

                            <video style={{width: '100%'}} controls poster={`${IMAGE_PATH}video-thumb-3.gif`} preload="metadata">
                            <source src="https://yeti-clean-media.lon1.cdn.digitaloceanspaces.com/before-after/roof_clean.mp4" type="video/mp4" />
                            </video>
                        </Box>
                        <Box sx={{...styles.fiftyFifty, ...styles.padding4, ...styles.vCenter, ...styles.whiteBox}}>
                            {!isMobile && <MultiRoofForm franchise={franchise}/>}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{...styles.parentBox, ...styles.darkBlueBox, ...styles.sectionDesktopOnlyRoof}}>
                    <Box sx={{...styles.oneHundred, ...styles.hCenter, ...styles.padding3}}>
                        <Typography level={"body-md"} sx={{maxWidth: "75%", fontWeight: 500}}>Ready to extend your roof’s lifespan and boost your home’s overall appeal?</Typography>
                    </Box>
                </Box>

                <Suspense fallback={<Box sx={{minHeight: '100vh', width: '100%'}} />}>
                    <BelowTheFoldContent
                        franchise={franchise}
                        scrollToForm={scrollToForm}
                        sliderSettings={sliderSettings}
                    />
                </Suspense>
            </Box>
        </>
    );
}